/**
 * Declarative config of « Mes sinistres » (`extranet.claimsListCard`, fiches
 * 22736 / 22989): stable columns, the server-side toolbar (search `q` +
 * status select `etat`, built with the shared `buildListToolbar`) and the
 * DataList config builder (inline rows, API order).
 *
 * Stable keys — the rule contract: columns `assure`, `produit`,
 * `numeroContrat`, `reference`, `dateSurvenance`, `circonstances`,
 * `coutAssureur`, `statut`; toolbar filter `statut`; the single search box.
 */

import type { ColumnConfig, DataListConfig } from '@components/integrated/data-list';

import type { ClaimListRow } from '../../api/claimsService';
import type { SelOption } from '../../api/listFilters';
import { EMPTY_STATE_TEXT } from '../shared/cardText';
import {
  EXTRANET_DATE_RENDERER,
  EXTRANET_ROW_OPEN_HANDLER,
  registerStatusBadgeRenderer,
  type ExtranetRowContext,
} from '../shared/cellRenderers';
import { buildListToolbar, type ListToolbarSpec } from '../shared/listToolbarConfig';
import { CLAIM_STATUS_BADGE, CLAIMS_TEXT } from './claimsText';

/** Renderer key of the `statut` column (ETAT → « Ouvert » / « Clos » badge). */
export const CLAIM_STATUS_RENDERER = registerStatusBadgeRenderer(
  'extranet-claimStatus',
  CLAIM_STATUS_BADGE,
);

/** Stable column keys of the claims list. */
export type ClaimsListColumnKey =
  | 'assure'
  | 'produit'
  | 'numeroContrat'
  | 'reference'
  | 'dateSurvenance'
  | 'circonstances'
  | 'coutAssureur'
  | 'statut';

/** Base columns (design order: assuré, produit, n° contrat, réf · date, circonstances, coût, statut). */
export const CLAIMS_LIST_COLUMNS: Readonly<Record<ClaimsListColumnKey, ColumnConfig>> = {
  assure: {
    label: 'Assuré',
    order: 1,
    type: 'text',
    className: 'font-medium',
    sortable: true,
  },
  produit: {
    label: 'Produit',
    order: 2,
    type: 'text',
    sortable: true,
  },
  numeroContrat: {
    label: 'N° contrat',
    order: 3,
    type: 'text',
    className: 'font-mono text-xs',
    sortable: true,
  },
  reference: {
    label: 'Référence',
    order: 4,
    type: 'text',
    className: 'font-mono text-xs',
    sortable: true,
  },
  dateSurvenance: {
    label: 'Survenance',
    headerTooltip: 'Date de survenance du sinistre',
    order: 5,
    type: 'custom',
    typeOptions: { renderer: EXTRANET_DATE_RENDERER },
    sortable: true,
  },
  circonstances: {
    label: 'Circonstances',
    order: 6,
    type: 'text',
    sortable: true,
  },
  coutAssureur: {
    label: 'Coût',
    headerTooltip: "Coût estimé pour l'assureur",
    order: 7,
    type: 'currency',
    typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    align: 'right',
    sortable: true,
  },
  statut: {
    label: 'Statut',
    order: 8,
    type: 'custom',
    typeOptions: { renderer: CLAIM_STATUS_RENDERER },
    sortable: true,
  },
};

/** Stable id of the status toolbar filter (sent as `etat`). */
export const CLAIMS_STATUS_FILTER_ID = 'statut';

/**
 * The toolbar of the list (fiche 22989): the search box and the status
 * select — « Tous statuts » (default, `etat` not sent) then the
 * `WS2_EA_SEL_STATUT_SINISTRE` options in API order. The page maps the
 * reported search to `q` and the select to `etat` (`buildClaimsListParams`).
 */
export function claimsListToolbarSpec(statusOptions: readonly SelOption[]): ListToolbarSpec {
  return {
    search: { placeholder: CLAIMS_TEXT.searchPlaceholder },
    status: {
      id: CLAIMS_STATUS_FILTER_ID,
      label: CLAIMS_TEXT.statusFilterLabel,
      allLabel: CLAIMS_TEXT.allStatuses,
      options: statusOptions,
    },
  };
}

/** A list row carrying its per-row context (`__ctx.onOpen`). */
export type ClaimsListDataRow = ClaimListRow & { __ctx?: ExtranetRowContext };

/**
 * Base DataList config of « Mes sinistres »: inline rows (already searched /
 * filtered by VEOS) in API order (no default sort), every row on one page,
 * client-side sort, clickable rows (each row's `__ctx.onOpen` opens the
 * claim).
 */
export function buildClaimsListConfig(
  componentId: string,
  rows: readonly ClaimsListDataRow[],
  statusOptions: readonly SelOption[] = [],
): DataListConfig {
  return {
    id: componentId,
    dataSource: { data: rows as unknown as Record<string, unknown>[] },
    columns: { ...CLAIMS_LIST_COLUMNS },
    display: { size: 'compact', hoverable: true, striped: true },
    sorting: { enabled: true },
    toolbar: buildListToolbar(claimsListToolbarSpec(statusOptions)),
    pagination: { mode: 'scroll', pageSize: 999_999 },
    actions: { rowClick: { handler: EXTRANET_ROW_OPEN_HANDLER } },
    i18n: { emptyMessage: EMPTY_STATE_TEXT },
  };
}
